import React, { useEffect, useState, useMemo } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MoneyIcon, FilePdfIcon, PencilSimpleIcon, PlayIcon, CheckCircleIcon, MagnifyingGlassIcon, PrinterIcon, FileXlsIcon, XIcon } from "@phosphor-icons/react";
import { downloadRowsAsXlsx, openPrintWindow, rowsToHtmlTable } from "@/lib/exportUtils";

const roleLabel = {
  admin: "Admin",
  manager: "Manager",
  front_desk: "Front Desk",
  housekeeping: "Housekeeping",
  restaurant_manager: "Restaurant Manager",
  service: "Service (Waiter)",
};

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function Payroll() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [salaries, setSalaries] = useState({}); // user_id -> row
  const [payslips, setPayslips] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(null); // user object
  const [ctcInput, setCtcInput] = useState("");

  const isAdmin = user?.role === "admin";

  const load = async () => {
    const [uRes, sRes, pRes] = await Promise.all([
      api.get("/users"),
      api.get("/payroll/salaries"),
      api.get("/payroll/payslips", { params: { month } }),
    ]);
    setUsers(uRes.data);
    const map = {};
    sRes.data.forEach((s) => { map[s.user_id] = s; });
    setSalaries(map);
    setPayslips(pRes.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const saveSalary = async () => {
    try {
      await api.put("/payroll/salaries", { user_id: editing.id, monthly_ctc: Number(ctcInput) || 0 });
      toast.success("Salary saved");
      setEditing(null); setCtcInput("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runPayroll = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/payroll/run?month=${month}`);
      toast.success(`Generated ${r.data.generated} payslip${r.data.generated === 1 ? "" : "s"} for ${month}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };

  const markPaid = async (slip) => {
    await api.patch(`/payroll/payslips/${slip.id}`, { status: "paid" });
    toast.success("Marked paid");
    load();
  };

  const downloadPdf = (slip) => {
    const token = localStorage.getItem("pms_token");
    fetch(`${API}/payroll/payslips/${slip.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `payslip-${slip.user_name.replace(/\s+/g, "-")}-${slip.month}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error("PDF download failed"));
  };

  // ---- Filters + export ----
  const [nameQ, setNameQ] = useState("");
  const [roleQ, setRoleQ] = useState("all");
  const clearFilters = () => { setNameQ(""); setRoleQ("all"); };
  const hasFilters = nameQ || roleQ !== "all";

  const matchesFilter = (name, role) => {
    if (roleQ !== "all" && role !== roleQ) return false;
    if (!nameQ.trim()) return true;
    return (name || "").toLowerCase().includes(nameQ.trim().toLowerCase());
  };

  const filteredPayslips = useMemo(() =>
    payslips.filter((s) => matchesFilter(s.user_name, s.user_role)),
    [payslips, nameQ, roleQ]);
  const filteredUsers = useMemo(() =>
    users.filter((u) => matchesFilter(u.name, u.role)),
    [users, nameQ, roleQ]);

  const payslipCols = [
    { key: "user_name", label: "Staff" },
    { key: "user_role_label", label: "Role" },
    { key: "monthly_ctc", label: "CTC (₹)", numeric: true, format: (v) => Number(v || 0).toLocaleString("en-IN") },
    { key: "working_days", label: "Working days", numeric: true },
    { key: "payable_days", label: "Payable days", numeric: true },
    { key: "present", label: "P", numeric: true },
    { key: "half_day", label: "½", numeric: true },
    { key: "leave", label: "L", numeric: true },
    { key: "absent", label: "A", numeric: true },
    { key: "net", label: "Net payable (₹)", numeric: true, format: (v) => Number(v || 0).toLocaleString("en-IN") },
    { key: "status", label: "Status" },
  ];
  const buildPayslipRows = () => filteredPayslips.map((s) => ({
    ...s,
    user_role_label: roleLabel[s.user_role] || s.user_role,
    leave: (s.paid_leave || 0) + (s.unpaid_leave || 0),
  }));

  const salaryCols = [
    { key: "name", label: "Staff" },
    { key: "email", label: "Email" },
    { key: "role_label", label: "Role" },
    { key: "ctc", label: "Monthly CTC (₹)", numeric: true, format: (v) => v === null || v === undefined ? "Not set" : Number(v).toLocaleString("en-IN") },
  ];
  const buildSalaryRows = () => filteredUsers.map((u) => ({
    name: u.name,
    email: u.email,
    role_label: roleLabel[u.role] || u.role,
    ctc: salaries[u.id]?.monthly_ctc ?? null,
  }));

  const exportPayslipsXlsx = () => {
    if (filteredPayslips.length === 0) return toast.error("Nothing to export");
    downloadRowsAsXlsx({ rows: buildPayslipRows(), columns: payslipCols, filename: `payslips-${month}`, sheetName: `Payslips ${month}` });
    toast.success(`Exported ${filteredPayslips.length} payslips`);
  };
  const printPayslips = () => {
    if (filteredPayslips.length === 0) return toast.error("Nothing to print");
    const html = rowsToHtmlTable({ rows: buildPayslipRows(), columns: payslipCols });
    const filterNote = hasFilters ? `${month} · filtered ${filteredPayslips.length} of ${payslips.length}` : `${month} · ${payslips.length} staff`;
    openPrintWindow({ title: `Payroll · ${month}`, subtitle: filterNote, bodyHtml: html });
  };
  const exportSalariesXlsx = () => {
    if (filteredUsers.length === 0) return toast.error("Nothing to export");
    downloadRowsAsXlsx({ rows: buildSalaryRows(), columns: salaryCols, filename: "salary-structure", sheetName: "Salaries" });
    toast.success(`Exported ${filteredUsers.length} salary rows`);
  };
  const printSalaries = () => {
    if (filteredUsers.length === 0) return toast.error("Nothing to print");
    const html = rowsToHtmlTable({ rows: buildSalaryRows(), columns: salaryCols });
    const filterNote = hasFilters ? `Filtered · ${filteredUsers.length} of ${users.length}` : `${users.length} staff`;
    openPrintWindow({ title: "Salary structure", subtitle: filterNote, bodyHtml: html });
  };

  return (
    <div>
      <PageHeader
        overline="People operations"
        title="Payroll"
        description="Monthly CTC-based payroll. Attendance auto-adjusts payable days."
      />

      <Card className="p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="payroll-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name…"
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            className="pl-9"
            data-testid="payroll-search"
          />
        </div>
        <Select value={roleQ} onValueChange={setRoleQ}>
          <SelectTrigger className="w-48" data-testid="payroll-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(roleLabel).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters} data-testid="payroll-clear-filters"><XIcon size={12} className="mr-1" />Clear</Button>
        )}
      </Card>

      <Tabs defaultValue="payslips" className="space-y-6">
        <TabsList>
          <TabsTrigger value="payslips" data-testid="payroll-tab-payslips">Payslips</TabsTrigger>
          <TabsTrigger value="salaries" data-testid="payroll-tab-salaries">Salary structure</TabsTrigger>
        </TabsList>

        <TabsContent value="payslips" className="space-y-6">
          <Card className="p-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Payroll month</Label>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-48" data-testid="payroll-month" />
              </div>
              <Button onClick={runPayroll} disabled={running || !isAdmin} data-testid="payroll-run-btn">
                <PlayIcon size={14} className="mr-1" /> {running ? "Running…" : `Run payroll for ${month}`}
              </Button>
              {!isAdmin && <p className="text-xs text-muted-foreground">Only admins can run payroll.</p>}
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={printPayslips} data-testid="payslips-print"><PrinterIcon size={14} className="mr-1" />Print</Button>
                <Button variant="outline" size="sm" onClick={exportPayslipsXlsx} data-testid="payslips-export-xlsx"><FileXlsIcon size={14} className="mr-1" />Excel</Button>
                <span className="text-xs text-muted-foreground" data-testid="payslips-result-count">{filteredPayslips.length} of {payslips.length}</span>
              </div>
            </div>
          </Card>

          <Card>
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="p-3 font-medium">Staff</th>
                  <th className="p-3 font-medium text-right">CTC</th>
                  <th className="p-3 font-medium text-right">Working / Payable</th>
                  <th className="p-3 font-medium text-right">Attendance (P/½/L/A)</th>
                  <th className="p-3 font-medium text-right">Net payable</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayslips.map((s) => (
                  <tr key={s.id} className="border-b border-border/50 last:border-b-0" data-testid={`payslip-row-${s.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7"><AvatarFallback>{s.user_name?.[0]}</AvatarFallback></Avatar>
                        <div>
                          <p className="font-medium">{s.user_name}</p>
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.user_role?.replace("_", " ")}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-right font-mono-plex">{inr(s.monthly_ctc)}</td>
                    <td className="p-3 text-right font-mono-plex">{s.working_days} / {s.payable_days}</td>
                    <td className="p-3 text-right text-xs text-muted-foreground font-mono-plex">
                      {s.present} · {s.half_day} · {s.paid_leave + s.unpaid_leave} · {s.absent}
                    </td>
                    <td className="p-3 text-right font-display text-lg">{inr(s.net)}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={s.status === "paid" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"}>
                        {s.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => downloadPdf(s)} title="Download payslip" data-testid={`payslip-pdf-${s.id}`}>
                          <FilePdfIcon size={14} />
                        </Button>
                        {isAdmin && s.status !== "paid" && (
                          <Button size="sm" variant="outline" onClick={() => markPaid(s)} data-testid={`payslip-mark-paid-${s.id}`}>
                            <CheckCircleIcon size={12} className="mr-1" /> Mark paid
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredPayslips.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">
                    <MoneyIcon size={28} className="mx-auto mb-2 opacity-40" />
                    {payslips.length === 0
                      ? `No payslips yet for ${month}. Run payroll to generate them.`
                      : `No payslips match your filter for ${month}.`}
                  </td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="salaries" className="space-y-6">
          <div className="flex items-center gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={printSalaries} data-testid="salaries-print"><PrinterIcon size={14} className="mr-1" />Print</Button>
            <Button variant="outline" size="sm" onClick={exportSalariesXlsx} data-testid="salaries-export-xlsx"><FileXlsIcon size={14} className="mr-1" />Excel</Button>
            <span className="text-xs text-muted-foreground" data-testid="salaries-result-count">{filteredUsers.length} of {users.length}</span>
          </div>
          <Card>
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="p-3 font-medium">Staff</th>
                  <th className="p-3 font-medium">Role</th>
                  <th className="p-3 font-medium text-right">Monthly CTC</th>
                  <th className="p-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const sal = salaries[u.id];
                  return (
                    <tr key={u.id} className="border-b border-border/50 last:border-b-0" data-testid={`salary-row-${u.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7"><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-xs uppercase tracking-wider text-muted-foreground">{u.role?.replace("_", " ")}</td>
                      <td className="p-3 text-right font-mono-plex">
                        {sal ? inr(sal.monthly_ctc) : <span className="text-muted-foreground italic">Not set</span>}
                      </td>
                      <td className="p-3 text-right">
                        {isAdmin && (
                          editing?.id === u.id ? (
                            <div className="flex justify-end items-center gap-2">
                              <Input type="number" value={ctcInput} onChange={(e) => setCtcInput(e.target.value)} className="w-32 h-8" data-testid={`salary-input-${u.id}`} autoFocus />
                              <Button size="sm" onClick={saveSalary} data-testid={`salary-save-${u.id}`}>Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setCtcInput(""); }}>Cancel</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => { setEditing(u); setCtcInput(sal ? sal.monthly_ctc : ""); }} data-testid={`salary-edit-${u.id}`}>
                              <PencilSimpleIcon size={12} className="mr-1" /> {sal ? "Edit" : "Set CTC"}
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
          <p className="text-xs text-muted-foreground">
            Monthly CTC is divided by the number of days in the payroll month to compute the per-day rate.
            Half-days count as 0.5, paid leaves are fully paid, absent and unpaid leaves are not paid.
            Days without any attendance mark are assumed to be worked.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
